/**
 * <HintCover> используем как обёртку для Hint'a с абсолютным позиционированием,
 * чтобы Hint мог правильно спозиционироваться.
 * Или используем любой блок с position: relative компонента, в котором вызываем Hint
 */

import React, { ReactNode, useRef } from 'react';

import { Button } from '../../common/Button';
import { IconSvg, Svg } from '../../common/IconSvg';
import { SvgProps } from '../../common/IconSvg/types';
import { helperViewHintList } from '../../helpers';
import { useOutsideClick } from '../../hooks/useOutsideClick';

import {
  HintContainer,
  HintInner,
  Icon,
  ButtonClose,
  arrowHorizontalLeft,
  arrowHorizontalRight,
  HintBackdrop,
  HintSkin
} from './styles';

const tuple = <T extends string[]>(...args: T) => args;

export const viewHint = tuple(
  'success',
  'warning',
  'error',
  'danger',
  'info',
  'alert',
  'buy',
  'sell'
);

export const positionHint = tuple(
  'default',
  'top',
  'bottom',
  'left',
  'right',
  'bottom-static',
  'absolute'
);

export const positionClose = tuple('top', 'middle');

export interface HintProps {
  view?: typeof viewHint[number];
  isImportant?: boolean;
  testId?: string;
  position?: typeof positionHint[number];
  icon?: ReactNode;
  isClosable?: boolean;
  closePosition?: typeof positionClose[number];
  onClose?: () => void;
  closeOnDocumentClick?: boolean;
}

const IconHintArrowLeft = (props: SvgProps) => (
  <Svg viewBox="0 0 35 48" preserveAspectRatio="none" {...props}>
    <path d="M35 48H28.7C19.5 48 17 44.5 12.6 39.4L0 24L12.7 8.7C15.2 5.5 16.6 4 18.2 2.8C19.6 1.7 21.2 1 22.9 0.5C24.7 0 26.7 0 30.5 0H35" />
  </Svg>
);

const IconHintArrowRight = (props: SvgProps) => (
  <Svg viewBox="0 0 35 48" preserveAspectRatio="none" {...props}>
    <path d="M0 48H6.3C15.5 48 18 44.5 22.4 39.4L35 24L22.3 8.7C19.8 5.5 18.4 4 16.8 2.8C15.4 1.7 13.8 1 12.1 0.5C10.3 0 8.3 0 4.5 0H0" />
  </Svg>
);

export const Hint: React.FC<HintProps> = props => {
  const {
    children,
    testId,
    view = 'success',
    position = 'default',
    icon,
    isImportant,
    isClosable,
    closePosition = 'middle',
    onClose,
    closeOnDocumentClick,
    ...other
  } = props;

  const containerRef = useRef<HTMLDivElement>(null);
  const [isClosed] = useOutsideClick(containerRef, closeOnDocumentClick, onClose);

  if (isClosed) {
    return null;
  }

  const renderArrow = (color?: string) => {
    if (position === 'left' || position === 'right') {
      return (
        <>
          {position === 'left' && (
            <IconHintArrowRight
              width={35}
              height="100%"
              color={color || helperViewHintList[view].backgroundColor}
              css={arrowHorizontalRight}
            />
          )}

          {position === 'right' && (
            <IconHintArrowLeft
              width={35}
              height="100%"
              color={color || helperViewHintList[view].backgroundColor}
              css={arrowHorizontalLeft}
            />
          )}
        </>
      );
    }

    return null;
  };

  return (
    <HintContainer
      ref={containerRef}
      data-testid={testId}
      role="alert"
      position={position}
      isImportant={isImportant}
      {...other}
    >
      {position !== 'default' && position !== 'bottom-static' && (
        <HintBackdrop view={view} position={position}>
          {renderArrow('inherit')}
        </HintBackdrop>
      )}
      <HintSkin view={view} position={position} isImportant={isImportant}>
        {renderArrow()}
        <HintInner position={position} isImportant={isImportant}>
          {icon && <Icon>{icon}</Icon>}
          <div>{children}</div>
          {isClosable && (
            <ButtonClose closePosition={closePosition}>
              <Button size="S" view="transparent" shape="circle" onClick={onClose}>
                <IconSvg src="close" color="inherit" />
              </Button>
            </ButtonClose>
          )}
        </HintInner>
      </HintSkin>
    </HintContainer>
  );
};
